import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ShieldCheck, ShieldOff, ShieldPlus, UserMinus, Mail, UserPlus,
  Loader2, AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, X, Pencil, KeyRound, Lock
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import { format } from 'date-fns';
import UserDetailModal from './UserDetailModal';

const ROLE_LABELS = { admin: 'ADMIN', user: 'CUSTOMER', staff: 'STAFF' };
const inputCls = 'bg-[#111115] border-white/10 text-white h-10 placeholder:text-white/20 focus:border-primary/60';

// ── Promote / Demote + Edit row ──────────────────────────────────────────────

function UserRoleRow({ user, adminCount, currentUserEmail, onChanged, onEdit }) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const isAdmin = user.role === 'admin';
  const isSelf = user.email === currentUserEmail;
  const isLastAdmin = isAdmin && adminCount <= 1;

  const handleToggle = async () => {
    if (!confirm) { setConfirm(true); return; }
    setLoading(true);
    setConfirm(false);
    try {
      await base44.functions.invoke('adminManageRole', {
        targetUserId: user.id,
        newRole: isAdmin ? 'user' : 'admin',
      });
      onChanged();
    } catch (e) {
      toast({ title: 'Role update failed', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const displayName = [user.first_name, user.last_name].filter(Boolean).join(' ') || user.full_name || user.email;

  return (
    <div className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-white/5 items-center hover:bg-white/[0.02] transition-colors min-w-[640px]">
      <div className="col-span-1">
        <div className={`w-8 h-8 flex items-center justify-center text-xs font-bold ${isAdmin ? 'bg-primary/10 text-primary' : 'bg-white/5 text-white/40'}`}>
          {displayName.charAt(0).toUpperCase()}
        </div>
      </div>
      <div className="col-span-3 min-w-0">
        <p className="text-white text-sm font-medium truncate">{displayName}</p>
        {isSelf && <p className="text-primary text-[10px] font-bold">YOU</p>}
      </div>
      <p className="col-span-3 text-white/40 text-xs truncate">{user.email}</p>
      <div className="col-span-2">
        <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold px-2 py-0.5 ${isAdmin ? 'bg-primary/10 text-primary' : 'bg-white/5 text-white/30'}`}>
          {isAdmin && <ShieldCheck className="w-3 h-3" />}
          {ROLE_LABELS[user.role] || 'CUSTOMER'}
        </span>
      </div>
      <div className="col-span-3 flex justify-end items-center gap-2">
        {!isSelf && (
          <button onClick={() => onEdit(user)} title="Edit admin"
            className="p-1.5 border border-white/10 text-white/40 hover:border-primary/40 hover:text-primary transition-all">
            <Pencil className="w-3.5 h-3.5" />
          </button>
        )}
        {isSelf ? (
          <span className="text-white/15 text-[10px]">—</span>
        ) : isLastAdmin ? (
          <span className="text-white/15 text-[10px]" title="Cannot remove the last admin">LAST</span>
        ) : confirm ? (
          <div className="flex items-center gap-1">
            <button onClick={handleToggle} disabled={loading}
              className="text-[10px] font-bold px-2 py-1 bg-red-600 hover:bg-red-500 text-white transition-colors">
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'CONFIRM'}
            </button>
            <button onClick={() => setConfirm(false)} className="text-white/30 hover:text-white p-1 transition-colors">
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <button onClick={handleToggle} disabled={loading}
            title={isAdmin ? 'Revoke admin' : 'Promote to admin'}
            className={`p-1.5 border transition-all ${isAdmin
              ? 'text-yellow-400/60 border-yellow-400/15 hover:border-yellow-400/50 hover:text-yellow-400'
              : 'text-white/30 border-white/10 hover:border-primary/40 hover:text-primary'}`}>
            {loading
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : isAdmin ? <UserMinus className="w-3.5 h-3.5" /> : <ShieldPlus className="w-3.5 h-3.5" />}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Invite panel ──────────────────────────────────────────────────────────────

function InviteAdminPanel({ onInvited }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    setResult(null);
    const res = await base44.functions.invoke('adminInviteAdmin', { email: email.trim().toLowerCase() });
    setLoading(false);
    if (res.data?.success) {
      setResult({ success: true, message: `Invite sent to ${email}. They will receive admin access on sign-up.` });
      setEmail('');
      onInvited?.();
    } else {
      setResult({ success: false, message: res.data?.error || 'Failed to send invite.' });
    }
  };

  return (
    <div className="border border-primary/20 bg-primary/5">
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 text-left">
        <div className="flex items-center gap-3">
          <Mail className="w-4 h-4 text-primary" />
          <div>
            <p className="text-primary text-xs font-bold tracking-widest">INVITE NEW ADMIN BY EMAIL</p>
            <p className="text-white/30 text-xs mt-0.5">Send a sign-up invite that auto-assigns the admin role</p>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-white/30" /> : <ChevronDown className="w-4 h-4 text-white/30" />}
      </button>

      {open && (
        <form onSubmit={handleInvite} className="px-5 pb-5 border-t border-primary/10 pt-4 space-y-4">
          <div className="flex flex-col sm:flex-row gap-3 items-end">
            <div className="flex-1 w-full">
              <p className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5">EMAIL ADDRESS *</p>
              <Input type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="newadmin@example.com" required
                className={inputCls} />
            </div>
            <button type="submit" disabled={loading || !email}
              className="flex items-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-50 text-white font-bold text-xs tracking-wider px-4 py-2.5 transition-colors h-10">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              {loading ? 'SENDING...' : 'SEND INVITE'}
            </button>
          </div>

          <div className="flex items-start gap-2 px-3 py-2 bg-white/5 border border-white/5">
            <AlertTriangle className="w-3.5 h-3.5 text-yellow-400 mt-0.5 shrink-0" />
            <p className="text-white/40 text-xs leading-relaxed">
              The invited user will receive a sign-up link. Once they register, their account will automatically be assigned the <span className="text-white font-bold">admin</span> role. If they already have an account, they will be promoted immediately.
            </p>
          </div>

          {result && (
            <div className={`flex items-start gap-2 px-3 py-2.5 border text-xs ${result.success ? 'bg-green-500/5 border-green-500/20 text-green-400' : 'bg-red-500/5 border-red-500/20 text-red-400'}`}>
              {result.success ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />}
              {result.message}
            </div>
          )}
        </form>
      )}
    </div>
  );
}

// ── Create admin with password panel ─────────────────────────────────────────

function CreateAdminPanel({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ full_name: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) return;
    setLoading(true);
    setResult(null);
    const res = await base44.functions.invoke('adminCreateAdmin', {
      email: form.email.trim().toLowerCase(),
      password: form.password,
      full_name: form.full_name.trim(),
    });
    setLoading(false);
    if (res.data?.success) {
      setResult({ success: true, message: `Admin account created for ${form.email}. A verification email was sent to them.` });
      setForm({ full_name: '', email: '', password: '' });
      onCreated?.();
    } else {
      setResult({ success: false, message: res.data?.error || 'Failed to create admin.' });
    }
  };

  return (
    <div className="border border-green-500/20 bg-green-500/5">
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 text-left">
        <div className="flex items-center gap-3">
          <UserPlus className="w-4 h-4 text-green-400" />
          <div>
            <p className="text-green-400 text-xs font-bold tracking-widest">CREATE ADMIN WITH PASSWORD</p>
            <p className="text-white/30 text-xs mt-0.5">Create a new admin account and set their initial password</p>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-white/30" /> : <ChevronDown className="w-4 h-4 text-white/30" />}
      </button>

      {open && (
        <form onSubmit={handleCreate} className="px-5 pb-5 border-t border-green-500/10 pt-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5">FULL NAME</p>
              <Input value={form.full_name} onChange={set('full_name')} placeholder="Jane Smith" className={inputCls} />
            </div>
            <div>
              <p className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5">EMAIL ADDRESS *</p>
              <Input type="email" value={form.email} onChange={set('email')} placeholder="newadmin@example.com" required className={inputCls} />
            </div>
          </div>
          <div>
            <p className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5">INITIAL PASSWORD * (min 6 chars)</p>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <Input type="text" value={form.password} onChange={set('password')} placeholder="Set a temporary password" required minLength={6}
                className={inputCls + ' pl-9'} />
            </div>
          </div>

          <div className="flex items-start gap-2 px-3 py-2 bg-white/5 border border-white/5">
            <AlertTriangle className="w-3.5 h-3.5 text-yellow-400 mt-0.5 shrink-0" />
            <p className="text-white/40 text-xs leading-relaxed">
              The new admin will receive a one-time verification email. Share the password with them securely — they can change it later from their account.
            </p>
          </div>

          {result && (
            <div className={`flex items-start gap-2 px-3 py-2.5 border text-xs ${result.success ? 'bg-green-500/5 border-green-500/20 text-green-400' : 'bg-red-500/5 border-red-500/20 text-red-400'}`}>
              {result.success ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />}
              {result.message}
            </div>
          )}

          <button type="submit" disabled={loading || !form.email || !form.password}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white font-bold text-xs tracking-wider px-4 py-2.5 transition-colors">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
            {loading ? 'CREATING...' : 'CREATE ADMIN'}
          </button>
        </form>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function AdminUserManager({ currentUser }) {
  const queryClient = useQueryClient();
  const [selectedUser, setSelectedUser] = useState(null);

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['admin-users-manager'],
    queryFn: () => base44.entities.User.list(),
  });

  const adminCount = users.filter(u => u.role === 'admin').length;
  const admins = users.filter(u => u.role === 'admin');

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-users-manager'] });
    queryClient.invalidateQueries({ queryKey: ['admin-users'] });
  };

  return (
    <div className="space-y-6">
      {/* Create with password */}
      <CreateAdminPanel onCreated={invalidate} />

      {/* Invite by email */}
      <InviteAdminPanel onInvited={invalidate} />

      {/* User list */}
      <div className="border border-white/5 overflow-x-auto">
        <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-[#111115] border-b border-white/5 min-w-[640px]">
          <div className="col-span-1" />
          <p className="col-span-3 text-white/30 text-[10px] font-bold tracking-widest">NAME</p>
          <p className="col-span-3 text-white/30 text-[10px] font-bold tracking-widest">EMAIL</p>
          <p className="col-span-2 text-white/30 text-[10px] font-bold tracking-widest">ROLE</p>
          <p className="col-span-3 text-white/30 text-[10px] font-bold tracking-widest text-right">ACTIONS</p>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-5 h-5 animate-spin text-primary" />
          </div>
        ) : (
          <>
            {admins.length > 0 && (
              <div>
                <div className="px-6 py-2 bg-primary/5 border-b border-primary/10">
                  <p className="text-primary text-[10px] font-bold tracking-widest flex items-center gap-1.5">
                    <ShieldCheck className="w-3 h-3" /> ADMINS ({admins.length})
                  </p>
                </div>
                {admins.map(u => (
                  <UserRoleRow key={u.id} user={u} adminCount={adminCount}
                    currentUserEmail={currentUser?.email}
                    onChanged={invalidate}
                    onEdit={setSelectedUser} />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <div className="px-4 py-3 bg-white/[0.02] border border-white/5">
        <p className="text-white/40 text-xs leading-relaxed">
          <span className="font-bold flex items-center gap-1.5 mb-1"><Pencil className="w-3.5 h-3.5" /> EDITING ADMINS</span>
          Click the edit icon next to any admin to update their name, contact details, role, send a password reset email, or remove the account. The platform sends password resets by secure email — you can set an initial password when creating a new admin above.
        </p>
      </div>

      {selectedUser && (
        <UserDetailModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onSave={(updatedData) => {
            setSelectedUser((prev) => ({ ...prev, ...updatedData }));
            invalidate();
          }}
        />
      )}
    </div>
  );
}